import type { ReactNode } from "react"
import { createContext, useContext, useState } from "react"

import type { ChangelogVersion } from "@productlane/zero"

import type { Language } from "../../settings/settings-pages/language-selector"
import { useSelectedLanguage } from "../../settings/settings-pages/language-selector"

interface ChangelogContextType {
	isDirty: boolean
	isSaving: boolean
	setIsDirty: (dirty: boolean) => void
	setIsSaving: (saving: boolean) => void
	onSave: (() => Promise<void>) | null
	setOnSave: (saveFn: (() => Promise<void>) | null) => void
	previewVersion: ChangelogVersion | null
	setPreviewVersion: (version: ChangelogVersion | null) => void
	selectedLanguage: Language
	setSelectedLanguage: (language: Language) => void
}

const ChangelogContext = createContext<ChangelogContextType | null>(null)

export const useChangelogContext = () => {
	const context = useContext(ChangelogContext)
	if (!context) {
		throw new Error(
			"useChangelogContext must be used within a ChangelogProvider",
		)
	}
	return context
}

export const useChangelogLanguage = () => {
	const context = useContext(ChangelogContext)
	if (!context) {
		throw new Error(
			"useChangelogLanguage must be used within a ChangelogProvider",
		)
	}
	return {
		selectedLanguage: context.selectedLanguage,
		setSelectedLanguage: context.setSelectedLanguage,
	}
}

interface ChangelogProviderProps {
	children: ReactNode
}

export const ChangelogProvider = ({ children }: ChangelogProviderProps) => {
	const [isDirty, setIsDirty] = useState(false)
	const [isSaving, setIsSaving] = useState(false)
	const [onSave, setOnSave] = useState<(() => Promise<void>) | null>(null)
	const [previewVersion, setPreviewVersion] = useState<ChangelogVersion | null>(
		null,
	)
	const [selectedLanguage, setSelectedLanguage] = useSelectedLanguage()

	return (
		<ChangelogContext.Provider
			value={{
				isDirty,
				isSaving,
				setIsDirty,
				setIsSaving,
				onSave,
				setOnSave,
				previewVersion,
				setPreviewVersion,
				selectedLanguage,
				setSelectedLanguage,
			}}
		>
			{children}
		</ChangelogContext.Provider>
	)
}
